import React, { useState } from "react";
import { Form, Input, Button, Card, Alert, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogout } from "features/user/userSlice";
import { changePassword } from "service/profile";

const ChangePassword = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onFinish = async (values) => {
    setErrors(null);
    setSubmitting(true);
    try {
      await changePassword(
        values.old_password,
        values.new_password,
        values.new_password2
      );
      message.success("Password changed. Please log in again with your new password.");
      // Force re-login: clear local state and redirect to /login.
      dispatch(setLogout(null));
      navigate("/login/");
    } catch (error) {
      const data = error?.response?.data || {};
      // Map server-side field errors into AntD form items.
      const fieldErrs = [];
      ["old_password", "new_password", "new_password2", "detail"].forEach((k) => {
        if (data[k]) {
          const msg = Array.isArray(data[k]) ? data[k].join(" ") : String(data[k]);
          if (k === "detail") {
            setErrors(msg);
          } else {
            fieldErrs.push({ name: k, errors: [msg] });
          }
        }
      });
      if (fieldErrs.length) {
        form.setFields(fieldErrs);
      } else if (!data.detail) {
        setErrors(error?.message || "Failed to change password.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 520, margin: "0 auto" }}>
      <Card title="Change password">
        {errors && (
          <Alert
            type="error"
            showIcon
            message={errors}
            style={{ marginBottom: 16 }}
          />
        )}
        <Alert
          type="info"
          showIcon
          message="You'll be signed out and asked to log in again with the new password."
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="old_password"
            label="Current password"
            rules={[{ required: true, message: "Please enter your current password" }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="New password"
            rules={[
              { required: true, message: "Please enter a new password" },
              { min: 8, message: "At least 8 characters" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="new_password2"
            label="Confirm new password"
            dependencies={["new_password"]}
            rules={[
              { required: true, message: "Please confirm the new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Update password
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ChangePassword;
